import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Phone, PhoneOff, Video, VideoOff, Mic, MicOff, PhoneIncoming, Minimize2, Maximize2, SwitchCamera } from "lucide-react";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/context/RealtimeContext";
import { logError } from "@/lib/log";

const CallContext = createContext(null);
export const useCall = () => useContext(CallContext);

// status: idle | calling | incoming | connecting | active
export function CallProvider({ children }) {
  const { user } = useAuth();
  const { subscribe, send } = useRealtime();

  const [call, setCall] = useState(null); // {id, peer, role, video}
  const [status, setStatus] = useState("idle");
  const [connState, setConnState] = useState("new");
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [remoteStream, setRemoteStream] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [voiceOnly, setVoiceOnly] = useState(false);
  const [callMinimized, setCallMinimized] = useState(false);
  const [cameraFacing, setCameraFacing] = useState("user");

  const pcRef = useRef(null);
  const localRef = useRef(null);
  const callIdRef = useRef(null);
  const callRef = useRef(null);
  const statusRef = useRef("idle");
  const pendingIce = useRef([]);
  const pendingOffer = useRef(null);
  const remoteSet = useRef(false);
  const remoteMediaStreamRef = useRef(null);

  useEffect(() => { callRef.current = call; }, [call]);
  useEffect(() => { statusRef.current = status; }, [status]);

  const cleanup = useCallback(() => {
    try {
      pcRef.current?.getSenders()?.forEach((sender) => sender.track?.stop());
      pcRef.current?.close();
    } catch (e) {
      logError("CallContext", e);
    }
    localRef.current?.getTracks()?.forEach((track) => track.stop());
    pcRef.current = null;
    if (typeof window !== "undefined") window.__yaprioPC = null;
    localRef.current = null;
    callIdRef.current = null;
    pendingIce.current = [];
    pendingOffer.current = null;
    remoteSet.current = false;
    remoteMediaStreamRef.current = null;
    setLocalStream(null);
    setRemoteStream(null);
    setConnState("new");
    setMicOn(true);
    setCamOn(true);
    setVoiceOnly(false);
    setCallMinimized(false);
    setCameraFacing("user");
  }, []);

  const hardReset = useCallback(() => {
    cleanup();
    callRef.current = null;
    statusRef.current = "idle";
    setStatus("idle");
    setCall(null);
  }, [cleanup]);

  const getMedia = useCallback(async (wantVideo) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("Media devices are not supported by this browser.");
    }

    if (wantVideo) {
      try {
        // Keep video calls as real video calls. Previously a camera error silently
        // downgraded the call to voice-only, which made it look like video calling
        // was broken while hiding the actual camera problem.
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30, max: 30 },
          },
        });
        if (!stream.getVideoTracks().length) {
          throw new Error("Camera did not provide a video track.");
        }
        setVoiceOnly(false);
        setCamOn(true);
        setCameraFacing(stream.getVideoTracks()[0]?.getSettings?.().facingMode || "user");
        return stream;
      } catch (err) {
        const messages = {
          NotAllowedError: "Camera permission is blocked. Allow camera access for yaprio.com and try again.",
          NotFoundError: "No camera was found on this device.",
          NotReadableError: "Your camera is already being used by another app.",
          OverconstrainedError: "The camera could not satisfy the video settings.",
          SecurityError: "Camera access is blocked by the browser security settings.",
        };
        throw new Error(messages[err.name] || err.message || "Could not access your camera.");
      }
    }

    return navigator.mediaDevices.getUserMedia({ audio: true, video: false });
  }, []);

  const endCall = useCallback(async (reason = "hung_up") => {
    const id = callIdRef.current || callRef.current?.id;
    const currentStatus = statusRef.current;
    const currentCall = callRef.current;
    if (id) {
      try {
        if (currentStatus === "calling" && currentCall?.role === "caller") {
          await api.post(`/calls/${id}/cancel`);
        } else {
          await api.post(`/calls/${id}/end`, { reason });
        }
      } catch (e) {
        logError("CallContext", e);
      }
    }
    hardReset();
  }, [hardReset]);

  const createPeer = useCallback(async () => {
    const { data } = await api.get("/calls/ice-config");
    const pc = new RTCPeerConnection({ iceServers: data.iceServers || [] });

    pc.onicecandidate = (event) => {
      if (event.candidate && callIdRef.current) {
        send({
          type: "webrtc:signal",
          call_id: callIdRef.current,
          kind: "ice",
          payload: event.candidate,
        });
      }
    };

    // Keep one remote MediaStream and add every incoming track to it. Some
    // browsers expose audio and video in different MediaStream objects, so
    // trusting event.streams[0] can leave the remote video out of the element.
    const remoteMediaStream = new MediaStream();
    remoteMediaStreamRef.current = remoteMediaStream;

    pc.ontrack = (event) => {
      const stream = remoteMediaStreamRef.current || remoteMediaStream;
      const existing = stream.getTracks().find((track) => track.kind === event.track.kind);
      if (existing && existing.id !== event.track.id) {
        stream.removeTrack(existing);
      }
      if (!stream.getTracks().some((track) => track.id === event.track.id)) {
        stream.addTrack(event.track);
      }
      event.track.onunmute = () => setRemoteStream(stream);
      event.track.onended = () => setRemoteStream(stream);
      setRemoteStream(stream);
    };

    pc.onconnectionstatechange = () => {
      setConnState(pc.connectionState);
      if (pc.connectionState === "connected") {
        setStatus("active");
        statusRef.current = "active";
      } else if (["failed", "closed"].includes(pc.connectionState)) {
        if (callIdRef.current) {
          toast("Call connection lost");
          endCall("connection_failed");
        }
      }
    };

    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === "failed" && callIdRef.current) {
        toast("Could not establish a direct connection");
      }
    };

    pcRef.current = pc;
    // Temporary WebRTC diagnostics: expose the active peer connection so we can
    // inspect SDP/transceivers while a call is live from DevTools.
    if (typeof window !== "undefined") window.__yaprioPC = pc;
    return pc;
  }, [send, endCall]);

  const drainIce = useCallback(async () => {
    const pc = pcRef.current;
    if (!pc) return;
    for (const candidate of pendingIce.current) {
      try {
        await pc.addIceCandidate(candidate);
      } catch (e) {
        logError("CallContext", e);
      }
    }
    pendingIce.current = [];
  }, []);

  const startCall = useCallback(async (peerUser, { video = true } = {}) => {
    if (statusRef.current !== "idle") {
      toast("You're already in a call");
      return;
    }

    let res;
    try {
      res = await api.post("/calls", { username: peerUser.username, video });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return;
    }

    const id = res.data.id;
    const callInfo = {
      id,
      peer: res.data.peer,
      role: "caller",
      video: res.data.video !== false,
    };
    callIdRef.current = id;
    callRef.current = callInfo;
    statusRef.current = "calling";
    setCall(callInfo);
    setStatus("calling");
    setCamOn(video);
    setVoiceOnly(!video);

    try {
      const stream = await getMedia(video);
      localRef.current = stream;
      setLocalStream(stream);
      const pc = await createPeer();
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));
      // addTrack() already creates the video transceiver. Keep that single
      // transceiver send/receive instead of adding a second recvonly one.
      if (video) {
        const videoTransceiver = pc.getTransceivers().find((t) => t.sender.track?.kind === "video");
        if (videoTransceiver) videoTransceiver.direction = "sendrecv";
      }

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      send({ type: "webrtc:signal", call_id: id, kind: "offer", payload: offer });
    } catch (err) {
      toast.error(
        err.name === "NotAllowedError"
          ? "Microphone permission is required to make a call."
          : "Could not access your microphone/camera."
      );
      try {
        await api.post(`/calls/${id}/end`, { reason: "media_error" });
      } catch (e) {
        logError("CallContext", e);
      }
      hardReset();
    }
  }, [getMedia, createPeer, send, hardReset]);

  const acceptCall = useCallback(async () => {
    const currentCall = callRef.current;
    if (!currentCall) return;

    try {
      await api.post(`/calls/${currentCall.id}/accept`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      hardReset();
      return;
    }

    callIdRef.current = currentCall.id;
    statusRef.current = "connecting";
    setStatus("connecting");
    const wantVideo = currentCall.video !== false;
    setCamOn(wantVideo);
    setVoiceOnly(!wantVideo);

    try {
      const stream = await getMedia(wantVideo);
      localRef.current = stream;
      setLocalStream(stream);
      const pc = await createPeer();
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));
      if (wantVideo) {
        const videoTransceiver = pc.getTransceivers().find((t) => t.sender.track?.kind === "video");
        if (videoTransceiver) videoTransceiver.direction = "sendrecv";
      }

      if (pendingOffer.current) {
        await pc.setRemoteDescription(new RTCSessionDescription(pendingOffer.current));
        remoteSet.current = true;
        await drainIce();
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        send({ type: "webrtc:signal", call_id: currentCall.id, kind: "answer", payload: answer });
        pendingOffer.current = null;
      }
    } catch (err) {
      toast.error(
        err.name === "NotAllowedError"
          ? "Microphone permission is required to answer a call."
          : "Could not access your microphone/camera."
      );
      try {
        await api.post(`/calls/${currentCall.id}/end`, { reason: "media_error" });
      } catch (e) {
        logError("CallContext", e);
      }
      hardReset();
    }
  }, [getMedia, createPeer, drainIce, send, hardReset]);

  const declineCall = useCallback(async () => {
    const currentCall = callRef.current;
    if (!currentCall) return;
    try {
      await api.post(`/calls/${currentCall.id}/decline`);
    } catch (e) {
      logError("CallContext", e);
    }
    hardReset();
  }, [hardReset]);

  const toggleMic = useCallback(() => {
    const track = localRef.current?.getAudioTracks()?.[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setMicOn(track.enabled);
  }, []);

  const toggleCam = useCallback(async () => {
    const existingTrack = localRef.current?.getVideoTracks()?.[0];
    if (existingTrack) {
      existingTrack.enabled = !existingTrack.enabled;
      setCamOn(existingTrack.enabled);
      return;
    }

    // A voice call can be upgraded in-place: acquire video, add its track,
    // and renegotiate the existing peer connection without interrupting audio.
    if (!pcRef.current || !localRef.current) return;
    let cameraStream;
    try {
      cameraStream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30, max: 30 } },
      });
      const track = cameraStream.getVideoTracks()[0];
      if (!track) throw new Error("No camera track was returned.");
      const pc = pcRef.current;
      const sender = pc.getSenders().find((item) => item.track?.kind === "video");
      if (sender) await sender.replaceTrack(track);
      else pc.addTrack(track, localRef.current);
      const stream = new MediaStream([...localRef.current.getAudioTracks(), track]);
      localRef.current = stream;
      setLocalStream(stream);
      setVoiceOnly(false);
      setCamOn(true);
      setCameraFacing(track.getSettings?.().facingMode || "user");
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      send({ type: "webrtc:signal", call_id: callIdRef.current, kind: "offer", payload: offer });
    } catch (err) {
      cameraStream?.getTracks().forEach((track) => track.stop());
      toast.error(err.name === "NotAllowedError"
        ? "Allow camera access for yaprio.com to turn on your camera."
        : err.name === "NotFoundError"
          ? "No camera was found on this device."
          : err.message || "Could not turn on your camera.");
    }
  }, [send]);

  const flipCamera = useCallback(async () => {
    const pc = pcRef.current;
    const currentStream = localRef.current;
    const oldTrack = currentStream?.getVideoTracks()?.[0];
    if (!pc || !currentStream) return;
    if (!oldTrack) {
      toast.info("Turn on your camera first.");
      return;
    }

    const sender = pc.getSenders().find((item) => item.track?.kind === "video");
    if (!sender) {
      toast.error("The video connection is not ready yet. Try again in a moment.");
      return;
    }

    const oldSettings = oldTrack.getSettings?.() || {};
    const oldDeviceId = oldSettings.deviceId;
    const oldFacing = oldSettings.facingMode || cameraFacing || "user";
    const nextFacing = oldFacing === "environment" ? "user" : "environment";
    let nextStream = null;

    try {
      const devices = (await navigator.mediaDevices.enumerateDevices())
        .filter((device) => device.kind === "videoinput");
      const otherDevice = devices.find((device) => device.deviceId && device.deviceId !== oldDeviceId);

      // Mobile browsers often cannot open the second camera while the first
      // camera is still active. Release it before requesting the replacement.
      oldTrack.stop();

      try {
        if (otherDevice) {
          nextStream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: {
              deviceId: { exact: otherDevice.deviceId },
              width: { ideal: 1280 },
              height: { ideal: 720 },
              frameRate: { ideal: 30, max: 30 },
            },
          });
        } else {
          nextStream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: {
              facingMode: { ideal: nextFacing },
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
          });
        }
      } catch (firstError) {
        // Fall back to facingMode on browsers whose device IDs are stale or
        // unavailable. Do not require an exact facing mode.
        nextStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: nextFacing },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });
      }

      const nextTrack = nextStream.getVideoTracks()[0];
      if (!nextTrack) throw new Error("The selected camera did not return a video track.");

      await sender.replaceTrack(nextTrack);
      const nextLocalStream = new MediaStream([...currentStream.getAudioTracks(), nextTrack]);
      localRef.current = nextLocalStream;
      setLocalStream(nextLocalStream);
      setCameraFacing(nextTrack.getSettings?.().facingMode || nextFacing);
      setCamOn(true);
    } catch (err) {
      nextStream?.getTracks().forEach((track) => track.stop());

      // Attempt to restore the original camera so a failed flip does not leave
      // the user's local video permanently off.
      try {
        const restoreStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: oldDeviceId
            ? { deviceId: { ideal: oldDeviceId }, width: { ideal: 1280 }, height: { ideal: 720 } }
            : { facingMode: { ideal: oldFacing }, width: { ideal: 1280 }, height: { ideal: 720 } },
        });
        const restoredTrack = restoreStream.getVideoTracks()[0];
        if (restoredTrack) {
          await sender.replaceTrack(restoredTrack);
          const restoredStream = new MediaStream([...currentStream.getAudioTracks(), restoredTrack]);
          localRef.current = restoredStream;
          setLocalStream(restoredStream);
          setCameraFacing(restoredTrack.getSettings?.().facingMode || oldFacing);
          setCamOn(true);
        }
      } catch (restoreError) {
        logError("CallContext camera restore", restoreError);
      }

      toast.error(err.name === "NotAllowedError"
        ? "Camera permission is blocked. Allow camera access and try again."
        : err.name === "NotFoundError" || err.name === "OverconstrainedError"
          ? "Could not start the other camera. This device may only expose one usable camera."
          : err.message || "Could not switch camera.");
    }
  }, [cameraFacing]);

  useEffect(() => {
    return subscribe(async (evt) => {
      switch (evt.type) {
        case "call:incoming": {
          if (statusRef.current !== "idle") {
            api.post(`/calls/${evt.call.id}/decline`).catch(() => {});
            return;
          }
          const incoming = {
            id: evt.call.id,
            peer: evt.call.caller,
            role: "callee",
            video: evt.call.video !== false,
          };
          // Set the ref immediately so an offer/ICE packet arriving right after
          // the incoming event is not discarded before React state updates.
          callIdRef.current = incoming.id;
          callRef.current = incoming;
          statusRef.current = "incoming";
          setCall(incoming);
          setStatus("incoming");
          setCamOn(incoming.video);
          setVoiceOnly(!incoming.video);
          break;
        }
        case "call:accepted":
          if (callIdRef.current === evt.call_id) {
            statusRef.current = "connecting";
            setStatus("connecting");
          }
          break;
        case "call:declined":
          if (callIdRef.current === evt.call_id) {
            toast("Call declined");
            hardReset();
          }
          break;
        case "call:cancelled":
          if (callIdRef.current === evt.call_id) {
            toast("Caller cancelled");
            hardReset();
          }
          break;
        case "call:ended":
          if (callIdRef.current === evt.call_id) {
            toast("Call ended");
            hardReset();
          }
          break;
        case "webrtc:signal": {
          if (evt.call_id !== callIdRef.current) return;
          const pc = pcRef.current;

          if (evt.kind === "offer") {
            if (evt.payload?.sdp?.includes("m=video")) {
              setVoiceOnly(false);
              setCall((current) => current ? { ...current, video: true } : current);
              if (callRef.current) callRef.current = { ...callRef.current, video: true };
            }
            if (!pc) {
              pendingOffer.current = evt.payload;
              return;
            }
            try {
              await pc.setRemoteDescription(new RTCSessionDescription(evt.payload));
              remoteSet.current = true;
              await drainIce();
              // Answer offers from either peer, including renegotiation when a
              // voice call is upgraded to video.
              if (pc.signalingState === "have-remote-offer") {
                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
                send({ type: "webrtc:signal", call_id: evt.call_id, kind: "answer", payload: answer });
              }
            } catch (e) {
              logError("CallContext", e);
            }
          } else if (evt.kind === "answer") {
            if (!pc) return;
            try {
              await pc.setRemoteDescription(new RTCSessionDescription(evt.payload));
              remoteSet.current = true;
              await drainIce();
            } catch (e) {
              logError("CallContext", e);
            }
          } else if (evt.kind === "ice") {
            if (pc && remoteSet.current) {
              try {
                await pc.addIceCandidate(evt.payload);
              } catch (e) {
                logError("CallContext", e);
              }
            } else {
              pendingIce.current.push(evt.payload);
            }
          }
          break;
        }
        default:
          break;
      }
    });
  }, [subscribe, send, drainIce, hardReset]);

  useEffect(() => {
    return () => {
      if (callIdRef.current) {
        // The server also ends calls when the authenticated WebSocket disconnects.
        cleanup();
      }
    };
  }, [cleanup]);

  const value = useMemo(() => ({ startCall, status, call }), [startCall, status, call]);

  return (
    <CallContext.Provider value={value}>
      {children}
      {status === "incoming" && call && (
        <IncomingCallModal call={call} onAccept={acceptCall} onDecline={declineCall} />
      )}
      {["calling", "connecting", "active"].includes(status) && call && (
        <InCallOverlay
          call={call}
          status={status}
          connState={connState}
          micOn={micOn}
          camOn={camOn}
          voiceOnly={voiceOnly}
          localStream={localStream}
          remoteStream={remoteStream}
          onToggleMic={toggleMic}
          onToggleCam={toggleCam}
          onFlipCamera={flipCamera}
          minimized={callMinimized}
          onMinimize={() => setCallMinimized(true)}
          onRestore={() => setCallMinimized(false)}
          onEnd={() => endCall("hung_up")}
        />
      )}
    </CallContext.Provider>
  );
}

function IncomingCallModal({ call, onAccept, onDecline }) {
  const video = call.video !== false;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" data-testid="incoming-call-modal">
      <div className="w-full max-w-sm rounded-3xl bg-white p-8 text-center shadow-2xl">
        <p className="mb-4 flex items-center justify-center gap-2 text-sm font-semibold text-pink-500">
          {video ? <Video className="h-4 w-4 animate-pulse" /> : <PhoneIncoming className="h-4 w-4 animate-pulse" />}
          Incoming {video ? "video" : "voice"} call
        </p>
        <Avatar className="mx-auto h-24 w-24 ring-4 ring-pink-100">
          <AvatarImage src={mediaUrl(call.peer?.avatar_url)} alt={call.peer?.username} />
          <AvatarFallback className="bg-pink-500 text-3xl font-bold text-white">
            {(call.peer?.username || "?").charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <h2 className="mt-4 font-heading text-2xl font-extrabold text-slate-900">@{call.peer?.username}</h2>
        <p className="text-sm text-slate-400">is calling you…</p>
        <div className="mt-8 flex items-center justify-center gap-6">
          <button onClick={onDecline} data-testid="btn-decline-call" className="flex flex-col items-center gap-1">
            <span className="flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full bg-red-500 text-white shadow-lg transition-transform hover:scale-105">
              <PhoneOff className="h-6 w-6" />
            </span>
            <span className="text-xs font-medium text-slate-500">Decline</span>
          </button>
          <button onClick={onAccept} data-testid="btn-accept-call" className="flex flex-col items-center gap-1">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500 text-white shadow-lg transition-transform hover:scale-105">
              <Phone className="h-6 w-6" />
            </span>
            <span className="text-xs font-medium text-slate-500">Accept</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusLabel({ status, connState }) {
  const map = { calling: "Ringing…", connecting: "Connecting…", active: "Connected" };
  const text = status === "active" ? connState.charAt(0).toUpperCase() + connState.slice(1) : map[status] || status;
  return (
    <span data-testid="call-connection-state" className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/80">
      {text}
    </span>
  );
}

function InCallOverlay({ call, status, connState, micOn, camOn, voiceOnly, localStream, remoteStream, onToggleMic, onToggleCam, onFlipCamera, minimized, onMinimize, onRestore, onEnd }) {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const remoteStreamRef = useRef(remoteStream);
  const dragRef = useRef(null);

  useEffect(() => {
    remoteStreamRef.current = remoteStream;
  }, [remoteStream]);

  const attachRemoteVideo = useCallback((node) => {
    remoteVideoRef.current = node;
    if (!node) return;
    node.srcObject = remoteStreamRef.current || null;
    if (remoteStreamRef.current) node.play().catch(() => {});
  }, []);
  const [position, setPosition] = useState(() => {
    if (typeof window === "undefined") return { x: 24, y: 80 };
    return { x: Math.max(8, window.innerWidth - 308), y: 80 };
  });

  useEffect(() => {
    if (localVideoRef.current && localStream) localVideoRef.current.srcObject = localStream;
  }, [localStream, minimized]);

  useEffect(() => {
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = remoteStream || null;
      if (remoteStream) remoteVideoRef.current.play().catch(() => {});
    }
  }, [remoteStream, minimized]);

  useEffect(() => {
    const audio = remoteAudioRef.current;
    if (!audio) return;
    audio.srcObject = remoteStream || null;
    if (remoteStream) audio.play().catch(() => {});
    return () => { if (audio) audio.srcObject = null; };
  }, [remoteStream, minimized]);

  const remoteVideoTracks = remoteStream?.getVideoTracks?.() || [];
  const hasRemoteVideo = remoteVideoTracks.some((track) => track.readyState !== "ended");

  if (minimized) {
    const handlePointerDown = (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      dragRef.current = { startX: event.clientX, startY: event.clientY, originX: position.x, originY: position.y };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    };

    const handlePointerMove = (event) => {
      if (!dragRef.current) return;
      const width = 300;
      const height = 205;
      const x = Math.min(Math.max(8, dragRef.current.originX + event.clientX - dragRef.current.startX), Math.max(8, window.innerWidth - width - 8));
      const y = Math.min(Math.max(8, dragRef.current.originY + event.clientY - dragRef.current.startY), Math.max(8, window.innerHeight - height - 8));
      setPosition({ x, y });
    };

    const stopDragging = () => { dragRef.current = null; };

    return (
      <div
        className="fixed z-[110] h-[205px] w-[300px] overflow-hidden rounded-2xl border border-white/20 bg-[#0d0d10] shadow-2xl"
        style={{ left: position.x, top: position.y, touchAction: "none" }}
        data-testid="minimized-call"
        onPointerMove={handlePointerMove}
        onPointerUp={stopDragging}
        onPointerCancel={stopDragging}
      >
        <div className="absolute inset-0 cursor-move" onPointerDown={handlePointerDown} aria-label="Drag call">
          {!voiceOnly ? (
            <video ref={attachRemoteVideo} autoPlay playsInline muted className="h-full w-full object-contain bg-black" data-testid="remote-video" onLoadedMetadata={(e) => e.currentTarget.play().catch(() => {})} onCanPlay={(e) => e.currentTarget.play().catch(() => {})} />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Avatar className="h-16 w-16 ring-2 ring-white/20">
                <AvatarImage src={mediaUrl(call.peer?.avatar_url)} alt={call.peer?.username} />
                <AvatarFallback className="bg-pink-500 font-bold text-white">{(call.peer?.username || "?").charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>
            </div>
          )}
        </div>
        {camOn && localStream && (
          <video ref={localVideoRef} autoPlay playsInline muted className="absolute bottom-3 right-3 h-20 w-14 rounded-lg border border-white/30 bg-black object-cover shadow-lg" data-testid="local-video-minimized" />
        )}
        <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" data-testid="remote-audio-minimized" />
        <div className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-1 text-[11px] font-semibold text-white backdrop-blur">@{call.peer?.username}</div>
        <div className="absolute right-2 top-2 flex gap-1">
          <button onPointerDown={(e) => e.stopPropagation()} onClick={onRestore} className="flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80" title="Restore call" data-testid="btn-restore-call"><Maximize2 className="h-4 w-4" /></button>
          <button onPointerDown={(e) => e.stopPropagation()} onClick={onEnd} className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600" title="End call" data-testid="btn-end-call-minimized"><PhoneOff className="h-4 w-4" /></button>
        </div>
        <div className="absolute bottom-2 left-2 rounded-full bg-black/60 px-2 py-1 text-[10px] font-medium text-white/80">{status === "active" ? "Connected" : status === "calling" ? "Ringing…" : "Connecting…"}</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-[#0d0d10]" data-testid="in-call-overlay">
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarImage src={mediaUrl(call.peer?.avatar_url)} alt={call.peer?.username} />
            <AvatarFallback className="bg-pink-500 font-bold text-white">
              {(call.peer?.username || "?").charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="font-heading font-bold text-white">@{call.peer?.username}</span>
        </div>
        <div className="flex items-center gap-2">
          <StatusLabel status={status} connState={connState} />
          <button onClick={onMinimize} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-white/80 transition hover:bg-white/20 hover:text-white" title="Minimize call" data-testid="btn-minimize-call"><Minimize2 className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="relative flex-1 overflow-hidden">
        {call.video !== false && !voiceOnly ? (
          <video
            ref={attachRemoteVideo}
            autoPlay
            playsInline
            muted
            className="h-full w-full object-contain bg-black"
            data-testid="remote-video"
            onLoadedMetadata={(e) => e.currentTarget.play().catch(() => {})}
            onCanPlay={(e) => e.currentTarget.play().catch(() => {})}
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-4">
            <Avatar className="h-28 w-28 ring-4 ring-white/10">
              <AvatarImage src={mediaUrl(call.peer?.avatar_url)} alt={call.peer?.username} />
              <AvatarFallback className="bg-pink-500 text-4xl font-bold text-white">
                {(call.peer?.username || "?").charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <p className="text-sm text-white/60">
              {voiceOnly ? "Voice call" : status === "calling" ? "Ringing…" : "Connecting…"}
            </p>
          </div>
        )}
        <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" data-testid="remote-audio" />

        <div className="absolute bottom-4 right-4 h-40 w-28 overflow-hidden rounded-2xl border border-white/20 bg-black shadow-lg sm:h-48 sm:w-36">
          {camOn && localStream ? (
            <video ref={localVideoRef} autoPlay playsInline muted className="h-full w-full object-cover" data-testid="local-video" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-white/40">
              <VideoOff className="h-6 w-6" />
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 p-4 sm:gap-4 sm:p-6">
        <button onClick={onToggleMic} data-testid="btn-toggle-mic" className={`flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full transition ${micOn ? "bg-white/15 text-white hover:bg-white/25" : "bg-white text-slate-900"}`}>
          {micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
        </button>
        <button onClick={onEnd} data-testid="btn-end-call" className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500 text-white shadow-lg transition hover:scale-105">
          <PhoneOff className="h-6 w-6" />
        </button>
        <button onClick={onToggleCam} data-testid="btn-toggle-cam" disabled={!localStream} className={`flex h-14 w-14 items-center justify-center rounded-full transition disabled:opacity-40 ${camOn ? "bg-white/15 text-white hover:bg-white/25" : "bg-white text-slate-900"}`}>
          {camOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
        </button>
        <button onClick={onFlipCamera} data-testid="btn-flip-camera" disabled={!camOn || !localStream?.getVideoTracks()?.length} className="flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25 disabled:opacity-40" title="Flip camera">
          <SwitchCamera className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
